import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { useSelector } from 'react-redux';
import { logoutUser } from '../../../store/slices/authSlice';
import { fetchMyPermissions, fetchModulesPermissions } from '../../../store/slices/adminSlice';
import { fetchUnreadCount } from '../../../store/slices/notificationSlice';
import toast from 'react-hot-toast';
import FXStaleAlert from './FXStaleAlert';

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { myPermissions } = useAppSelector((state) => state.admin);
  const { unreadCount } = useSelector((state) => state.notifications);
  const [expandedMenu, setExpandedMenu] = useState('User Management');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (user && (user.user_type === 'SubAdmin')) {
      dispatch(fetchMyPermissions());
      dispatch(fetchModulesPermissions());
    }
  }, [dispatch, user]);

  useEffect(() => {
    dispatch(fetchUnreadCount());
  }, [dispatch]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      toast.success('Logged out successfully');
      navigate('/');
    } catch {
      toast.error('Logout failed');
    }
  };

  // Check if user has revenue config permissions (view or edit)
  const hasRevenueConfigPermission = () => {
    // Admins always have access
    if (user?.user_type === 'Admin') {
      return true;
    }

    // For SubAdmins, check if they have revenue_config permissions
    // Permission IDs 133-136 are for revenue config (index, add, edit, view)
    if (myPermissions && Array.isArray(myPermissions.permission_ids) && myPermissions.permission_ids.length > 0) {
      return myPermissions.permission_ids.some(id => [133, 134, 135, 136].includes(Number(id)));
    }

    return false;
  };

  const isActive = (path) => location.pathname === path;

  const toggleMenu = (menuName) =>
    setExpandedMenu((prev) => (prev === menuName ? null : menuName));

  const isMenuActive = (paths) => {
    // Handle both array of strings and array of objects
    return paths.some(pathItem => {
      const path = typeof pathItem === 'string' ? pathItem : pathItem.path;
      return location.pathname === path || location.pathname.startsWith(path + '/');
    });
  };

  const isPathActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Special function to check if we're in contract-related pages
  const isContractActive = () => {
    return location.pathname === '/admin/contracts' ||
      location.pathname.startsWith('/admin/contract/');
  };

  // Special function to check if we're in flight management related pages
  const isFlightManagementActive = () => {
    return location.pathname === '/admin/admin-flights' ||
      location.pathname.startsWith('/admin/admin-flights/') ||
      location.pathname === '/admin/flight-charges' ||
      location.pathname.startsWith('/admin/aircraft/route/');
  };

  return (
    <div className="min-h-screen bg-[#f5f8fc]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-[50px] bg-white z-[1030] shadow-sm">
        <div className="flex items-center h-full justify-between">
          <div className="flex items-center h-full">
            <button
              type="button"
              onClick={() => setSidebarOpen((open) => !open)}
              className="md:hidden flex items-center justify-center w-12 h-[50px] text-gray-700 hover:bg-gray-100"
              aria-label="Toggle menu"
            >
              <i className={`fas ${sidebarOpen ? 'fa-times' : 'fa-bars'} text-lg`}></i>
            </button>
            <Link
              to="/admin"
              className="w-[230px] max-md:w-auto h-[50px] flex items-center justify-center bg-white text-lg font-bold text-gray-800 no-underline max-md:px-2"
            >
              <img src="/jetscanner-logo.svg" alt="JetScanner360" className="h-8 w-auto" />
            </Link>
          </div>
          <div className="flex items-center gap-2 px-4">
            <img
              src={user?.image_url || user?.image || '/images/default-avatar.png'}
              className="w-[30px] h-[30px] rounded-full object-cover"
              alt="User"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23666"%3E%3Cpath d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/%3E%3C/svg%3E';
              }}
            />
            <span className="text-sm font-medium capitalize max-sm:hidden">{user?.first_name || ''}</span>
          </div>
        </div>
      </header>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[809] md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-[50px] left-0 w-[230px] h-[calc(100vh-50px)] bg-[#f5f8fc] z-[810] overflow-y-auto transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } md:translate-x-0`}
      >
        <nav className="py-3 px-2">
          <ul className="space-y-1">

            {/* User Management */}
            <li>
              <button
                onClick={() => toggleMenu('User Management')}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded text-sm transition-colors ${isMenuActive(['/admin/users', '/admin/airliners', '/admin/jetsetters', '/admin/sax-users'])
                  ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                  : 'text-gray-700 hover:bg-[rgb(255,210,0)] hover:text-black'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <i className="fas fa-user text-[#e8e814] text-sm w-5"></i>
                  <span className="text-[13px] font-medium">User Management</span>
                </div>
                <i className={`fas fa-angle-left text-xs transition-transform ${expandedMenu === 'User Management' ? '-rotate-90' : ''}`}></i>
              </button>

              {expandedMenu === 'User Management' && (
                <ul className="mt-1 ml-4 space-y-0.5">
                  <li>
                    <Link
                      to="/admin/users"
                      className={`flex items-center gap-2 px-3 py-2 rounded text-xs transition-colors no-underline ${isPathActive('/admin/users')
                        ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                        : 'text-gray-600 hover:bg-[rgb(255,210,0)] hover:text-black'
                        }`}
                    >
                      <i className="fas fa-users text-[#cc7c25cf] text-sm w-5"></i>
                      <span>All</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/admin/airliners"
                      className={`flex items-center gap-2 px-3 py-2 rounded text-xs transition-colors no-underline ${isPathActive('/admin/airliners')
                        ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                        : 'text-gray-600 hover:bg-[rgb(255,210,0)] hover:text-black'
                        }`}
                    >
                      <i className="fas fa-plane-departure text-[#257ecccf] text-sm w-5"></i>
                      <span>Airliners</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/admin/jetsetters"
                      className={`flex items-center gap-2 px-3 py-2 rounded text-xs transition-colors no-underline ${isPathActive('/admin/jetsetters')
                        ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                        : 'text-gray-600 hover:bg-[rgb(255,210,0)] hover:text-black'
                        }`}
                    >
                      <i className="far fa-address-book text-[#97cc25cf] text-sm w-5"></i>
                      <span>JetSetters</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/admin/sax-users"
                      className={`flex items-center gap-2 px-3 py-2 rounded text-xs transition-colors no-underline ${isPathActive('/admin/sax-users')
                        ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                        : 'text-gray-600 hover:bg-[rgb(255,210,0)] hover:text-black'
                        }`}
                    >
                      <i className="fas fa-user text-[#cc25b2cf] text-sm w-5"></i>
                      <span>SubAdmin</span>
                    </Link>
                  </li>
                </ul>
              )}
            </li>

            {/* Contract Management */}
            <li>
              <Link
                to="/admin/contracts"
                className={`flex items-center gap-2 px-3 py-2.5 rounded text-sm transition-colors no-underline ${isContractActive()
                  ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                  : 'text-gray-700 hover:bg-[rgb(255,210,0)] hover:text-black'
                  }`}
              >
                <i className="fas fa-user-cog text-[#2a2af0] text-sm w-5"></i>
                <span className="text-[13px] font-medium">Contract Management</span>
              </Link>
            </li>

            {/* CMS */}
            <li>
              <Link
                to="/admin/cms"
                className={`flex items-center gap-2 px-3 py-2.5 rounded text-sm transition-colors no-underline ${location.pathname.startsWith('/admin/cms')
                  ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                  : 'text-gray-700 hover:bg-[rgb(255,210,0)] hover:text-black'
                  }`}
              >
                <i className="far fa-address-book text-[#8825cccf] text-sm w-5"></i>
                <span className="text-[13px] font-medium">CMS</span>
              </Link>
            </li>

            {/* Flight Management */}
            <li>
              <Link
                to="/admin/admin-flights"
                className={`flex items-center gap-2 px-3 py-2.5 rounded text-sm transition-colors no-underline ${isFlightManagementActive()
                  ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                  : 'text-gray-700 hover:bg-[rgb(255,210,0)] hover:text-black'
                  }`}
              >
                <i className="fas fa-plane text-[#f39c12] text-sm w-5"></i>
                <span className="text-[13px] font-medium">Flight Management</span>
              </Link>
            </li>

            {/* Booking Management — live DB list & totals (not Looker / not airliner net revenue) */}
            <li>
              <Link
                to="/admin/bookings"
                className={`flex items-center gap-2 px-3 py-2.5 rounded text-sm transition-colors no-underline ${isPathActive('/admin/bookings')
                  ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                  : 'text-gray-700 hover:bg-[rgb(255,210,0)] hover:text-black'
                  }`}
              >
                <i className="fas fa-ticket-alt text-[#8e44ad] text-sm w-5"></i>
                <span className="text-[13px] font-medium">Booking Management</span>
              </Link>
            </li>

            {/* Refund Management */}
            <li>
              <button
                onClick={() => toggleMenu('Refund Management')}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded text-sm transition-colors ${isMenuActive(['/admin/refunds', '/admin/refund-history'])
                  ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                  : 'text-gray-700 hover:bg-[rgb(255,210,0)] hover:text-black'}}`}
              >
                <div className="flex items-center gap-2">
                  <i className="fas fa-money-bill-wave text-[#27ae60] text-sm w-5"></i>
                  <span className="text-[13px] font-medium">Refund Management</span>
                </div>
                <i className={`fas fa-angle-left text-xs transition-transform ${expandedMenu === 'Refund Management' ? '-rotate-90' : ''}`}></i>
              </button>

              {expandedMenu === 'Refund Management' && (
                <ul className="mt-1 ml-4 space-y-0.5">
                  <li>
                    <Link
                      to="/admin/refunds"
                      className={`flex items-center gap-2 px-3 py-2 rounded text-xs transition-colors no-underline ${isPathActive('/admin/refunds')
                        ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                        : 'text-gray-600 hover:bg-[rgb(255,210,0)] hover:text-black'
                        }`}
                    >
                      <i className="fas fa-hourglass-half text-[#f39c12] text-sm w-5"></i>
                      <span>Pending Refunds</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/admin/refund-history"
                      className={`flex items-center gap-2 px-3 py-2 rounded text-xs transition-colors no-underline ${isPathActive('/admin/refund-history')
                        ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                        : 'text-gray-600 hover:bg-[rgb(255,210,0)] hover:text-black'
                        }`}
                    >
                      <i className="fas fa-history text-[#3498db] text-sm w-5"></i>
                      <span>Refund History</span>
                    </Link>
                  </li>
                </ul>
              )}
            </li>

            {/* Data Analysis */}
            <li>

              <button

                onClick={() => toggleMenu('Data Analysis')}

                className={`w-full flex items-center justify-between px-3 py-2.5 rounded text-sm transition-colors ${isMenuActive([

                  { path: '/admin/data-analysis/flight-search' },

                  { path: '/admin/data-analysis/flight-details' },

                  { path: '/admin/data-analysis/pages-view' },

                  { path: '/admin/data-analysis/user-auth' },

                  { path: '/admin/data-analysis/booking-process' }

                ])

                  ? 'bg-[rgb(255,210,0)] text-black shadow-sm'

                  : 'text-gray-700 hover:bg-[rgb(255,210,0)] hover:text-black'

                  }`}

              >

                <div className="flex items-center gap-2">

                  <i className="fas fa-gauge-high text-[#ffc107] text-sm w-5"></i>

                  <span className="text-[13px] font-medium">Data Analysis</span>

                </div>

                <i className={`fas fa-angle-left text-xs transition-transform ${expandedMenu === 'Data Analysis' ? '-rotate-90' : ''}`}></i>

              </button>



              {expandedMenu === 'Data Analysis' && (

                <ul className="mt-1 ml-4 space-y-0.5">

                  <li>

                    <Link

                      to="/admin/data-analysis/flight-search"

                      className={`flex items-center gap-2 px-3 py-2 rounded text-xs transition-colors no-underline ${isActive('/admin/data-analysis/flight-search')

                        ? 'bg-[rgb(255,210,0)] text-black shadow-sm'

                        : 'text-gray-600 hover:bg-[rgb(255,210,0)] hover:text-black'

                        }`}

                    >

                      <i className="fas fa-gauge-high text-[#366a77] text-sm w-5"></i>

                      <span>Flight Search</span>

                    </Link>

                  </li>

                  <li>

                    <Link

                      to="/admin/data-analysis/flight-details"

                      className={`flex items-center gap-2 px-3 py-2 rounded text-xs transition-colors no-underline ${isActive('/admin/data-analysis/flight-details')

                        ? 'bg-[rgb(255,210,0)] text-black shadow-sm'

                        : 'text-gray-600 hover:bg-[rgb(255,210,0)] hover:text-black'

                        }`}

                    >

                      <i className="fas fa-gauge-high text-[#366a77] text-sm w-5"></i>

                      <span>Flight Details</span>

                    </Link>

                  </li>

                  <li>

                    <Link

                      to="/admin/data-analysis/pages-view"

                      className={`flex items-center gap-2 px-3 py-2 rounded text-xs transition-colors no-underline ${isActive('/admin/data-analysis/pages-view')

                        ? 'bg-[rgb(255,210,0)] text-black shadow-sm'

                        : 'text-gray-600 hover:bg-[rgb(255,210,0)] hover:text-black'

                        }`}

                    >

                      <i className="fas fa-gauge-high text-[#366a77] text-sm w-5"></i>

                      <span>Pages View</span>

                    </Link>

                  </li>

                  <li>

                    <Link

                      to="/admin/data-analysis/user-auth"

                      className={`flex items-center gap-2 px-3 py-2 rounded text-xs transition-colors no-underline ${isActive('/admin/data-analysis/user-auth')

                        ? 'bg-[rgb(255,210,0)] text-black shadow-sm'

                        : 'text-gray-600 hover:bg-[rgb(255,210,0)] hover:text-black'

                        }`}

                    >

                      <i className="fas fa-gauge-high text-[#366a77] text-sm w-5"></i>

                      <span>User Auth</span>

                    </Link>

                  </li>

                  <li>

                    <Link

                      to="/admin/data-analysis/booking-process"

                      className={`flex items-center gap-2 px-3 py-2 rounded text-xs transition-colors no-underline ${isActive('/admin/data-analysis/booking-process')

                        ? 'bg-[rgb(255,210,0)] text-black shadow-sm'

                        : 'text-gray-600 hover:bg-[rgb(255,210,0)] hover:text-black'

                        }`}

                    >

                      <i className="fas fa-gauge-high text-[#366a77] text-sm w-5"></i>

                      <span>Booking Process</span>

                    </Link>

                  </li>

                </ul>

              )}

            </li>

            {/* Account Settings */}
            <li>
              <Link
                to="/admin/audit-logs"
                className={`flex items-center gap-2 px-3 py-2.5 rounded text-sm transition-colors no-underline ${isPathActive('/admin/audit-logs')
                  ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                  : 'text-gray-700 hover:bg-[rgb(255,210,0)] hover:text-black'
                  }`}
              >
                <i className="fas fa-history text-[#0066cc] text-sm w-5"></i>
                <span className="text-[13px] font-medium">Audit Logs</span>
              </Link>
            </li>

            {/* Account Settings */}
            <li>
              <Link
                to="/admin/settings"
                className={`flex items-center gap-2 px-3 py-2.5 rounded text-sm transition-colors no-underline ${isPathActive('/admin/settings')
                  ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                  : 'text-gray-700 hover:bg-[rgb(255,210,0)] hover:text-black'
                  }`}
              >
                <i className="fas fa-gears text-[#f70505] text-sm w-5"></i>
                <span className="text-[13px] font-medium">Account Settings</span>
              </Link>
            </li>

            {/* Messages */}
            <li>
              <Link
                to="/admin/notification"
                className={`flex items-center gap-2 px-3 py-2.5 rounded text-sm transition-colors no-underline ${isPathActive('/admin/notification')
                  ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                  : 'text-gray-700 hover:bg-[rgb(255,210,0)] hover:text-black'
                  }`}
              >
                <i className="fas fa-bell text-[#000000] text-sm w-5"></i>
                <span className="text-[13px] font-medium">Messages</span>
                {unreadCount > 0 && (
                  <span className="bg-red-600 text-white rounded-full w-[18px] h-[18px] flex items-center justify-center text-[10px] font-semibold ml-auto">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>
            </li>

            {/* Reactivation Requests */}
            <li>
              <Link
                to="/admin/reactivation-requests"
                className={`flex items-center gap-2 px-3 py-2.5 rounded text-sm transition-colors no-underline ${isPathActive('/admin/reactivation-requests')
                  ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                  : 'text-gray-700 hover:bg-[rgb(255,210,0)] hover:text-black'
                  }`}
              >
                <i className="fas fa-user-check text-[#000000] text-sm w-5"></i>
                <span className="text-[13px] font-medium">Reactivation Requests</span>
              </Link>
            </li>

            {/* Deletion Requests */}
            <li>
              <Link
                to="/admin/deletion-requests"
                className={`flex items-center gap-2 px-3 py-2.5 rounded text-sm transition-colors no-underline ${isPathActive('/admin/deletion-requests')
                  ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                  : 'text-gray-700 hover:bg-[rgb(255,210,0)] hover:text-black'
                  }`}
              >
                <i className="fas fa-user-times text-[#dc2626] text-sm w-5"></i>
                <span className="text-[13px] font-medium">Deletion Requests</span>
              </Link>
            </li>

            {/* Blogs */}
            <li>
              <Link
                to="/admin/blogs"
                className={`flex items-center gap-2 px-3 py-2.5 rounded text-sm transition-colors no-underline ${isPathActive('/admin/blogs')
                  ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                  : 'text-gray-700 hover:bg-[rgb(255,210,0)] hover:text-black'
                  }`}
              >
                <i className="fas fa-blog text-[#000] text-sm w-5"></i>
                <span className="text-[13px] font-medium">Blogs</span>
              </Link>
            </li>

            {/* Revenue Configuration - Only show if user has permission */}
            {hasRevenueConfigPermission() && (
              <li>
                <Link
                  to="/admin/revenue-config"
                  className={`flex items-center gap-2 px-3 py-2.5 rounded text-sm transition-colors no-underline ${isPathActive('/admin/revenue-config')
                    ? 'bg-[rgb(255,210,0)] text-black shadow-sm'
                    : 'text-gray-700 hover:bg-[rgb(255,210,0)] hover:text-black'
                    }`}
                >
                  <i className="fas fa-dollar-sign text-[#27ae60] text-sm w-5"></i>
                  <span className="text-[13px] font-medium">Revenue Config</span>
                </Link>
              </li>
            )}

            {/* Logout — always visible */}
            <li>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded text-sm transition-colors text-gray-700 hover:bg-[rgb(255,210,0)] hover:text-black"
              >
                <i className="fas fa-sign-out-alt text-[#24b324] text-sm w-5"></i>
                <span className="text-[13px] font-medium">Logout</span>
              </button>
            </li>

          </ul>
        </nav>
      </aside>

      {/* Content */}
      <div className="min-h-[calc(100vh-50px)] bg-[#f5f8fc] mt-[50px] ml-[230px] max-md:ml-0">
        <div className="p-4 max-sm:p-3">
          <FXStaleAlert />
        </div>
        {children}
        <footer className="py-6 px-4 bg-[#f5f8fc] border-t border-gray-200">
          <div className="text-center text-[#555] text-sm">
            Copyright © 2026 Jetscanner360. All rights reserved.
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AdminLayout;
